import React, {useContext, useMemo} from 'react';
import { Box, Stack, Card, CardContent, Skeleton } from '@mui/material';
import SelectedFaxContext from './SelectedFaxContext';
import {MyTypography} from '../../common/components';
import ReceiveFaxListItem from './ReceiveFaxListItem';
import DateTimeUtils from '../../common/services/DateTimeUtils';
import useCurrentClinicTimeFormat from '../../common/hooks/useCurrentClinicTimeFormat';


function ReceivedFaxListSkeleton({length=4}) {
    return (
        <Stack direction="column"
               spacing={1}
               alignItems="flex-start"
               justifyContent="flex-start"
               sx={{px: 1, py: 2}}>
            {
                Array.from({ length }, (_, i) => i).map((value) => (
                    <Card key={`list-item-skeleton-${value}`}
                          sx={{
                              width: '100%',
                              mb: 1.5,
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              border: 1,
                              borderColor: 'divider',
                              bgcolor: 'background.paper',
                          }}>
                        <CardContent sx={{ p: 2 }}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                sx={{ mb: 1 }}>
                                <Skeleton animation="wave" sx={{minWidth: 28, minHeight: 38}} />
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Skeleton animation="wave" />
                                    <Skeleton animation="wave" />
                                </Box>
                            </Stack>
                            <Stack direction="row"
                                   justifyContent="space-between"
                                   alignItems="center">
                                <Skeleton animation="wave" sx={{width: 100}} />
                            </Stack>
                        </CardContent>
                    </Card>
                ))
            }
        </Stack>
    );
}

function NoReceivedFaxesList() {
    return (
        <Card sx={{width: '100%'}}>
            <CardContent sx={{py: 2}}>
                <MyTypography variant="caption" component="p" align="center">No faxes available</MyTypography>
            </CardContent>
        </Card>
    );
}

export default function ReceivedFaxList({data, loading, onDeleteFax, onClickFaxItem, components}) {
    const selectedFax = useContext(SelectedFaxContext);
    const currentClinicTimeFormat = useCurrentClinicTimeFormat();

    const ListItemComponent = useMemo(() => {
        if (components && components.hasOwnProperty('listItem')) {
            return components.listItem;
        }
        return ReceiveFaxListItem;
    }, [components])

    if (loading || !data) {
        return (<ReceivedFaxListSkeleton />);
    }

    return (
        <Stack direction="column"
               spacing={1}
               alignItems="flex-start"
               justifyContent="flex-start"
               sx={{px: 1, py: 2}}>
            {
                data.inbound_faxes.length === 0
                ? <NoReceivedFaxesList />
                : data.inbound_faxes.map(fax => {
                    const createdLocalTime = DateTimeUtils.parse(fax.metadata.date_created);
                    const createdLocalTimeFormatted = DateTimeUtils.formatDisplayDateTime(
                              createdLocalTime,
                              currentClinicTimeFormat,
                              true,
                          ),
                          createdAgo = createdLocalTime.fromNow();

                    return (
                        <ListItemComponent
                            key={`${fax.source}-${fax.external_id}`}
                            {...fax}
                            fromNumber={fax.metadata.from_number}
                            dateCreated={createdLocalTimeFormatted}
                            createdAgo={createdAgo}
                            status={fax.status}
                            selected={selectedFax?.external_id === fax.external_id}
                            onClickFaxItem={onClickFaxItem(fax)}
                            onDeleteFax={onDeleteFax(fax.external_id)}
                        />
                    );
                })
            }
        </Stack>
    );
}
